import { createFileRoute, Link } from '@tanstack/react-router'
import { Check, Copy, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useWorkspace } from '@/api/workspace'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

// Panduan → MCP (ADR 0004, #26): how an Owner connects ChatGPT or Claude to Tridi's MCP server, what
// the AI can do, worked usage examples, and what to do after an AI-created Product. Static text from
// `guide.mcp.*` in id.json; the AI apps' menu names stay in their own (English) labels. Staff see it
// too, with a note that only the Owner can connect.
export const Route = createFileRoute('/w/$slug/guide/mcp')({
  component: McpGuideScreen,
  staticData: { breadcrumb: 'guide.mcp.nav' },
})

const CLIENTS = [
  { key: 'chatgpt', steps: 4 },
  { key: 'claude', steps: 4 },
] as const

const EXAMPLES = ['lowStock', 'cogs', 'compare', 'create', 'slicer'] as const

function McpGuideScreen() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const isOwner = useWorkspace(slug).data?.role === 'owner'

  return (
    <section className="flex max-w-4xl flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="font-heading text-xl font-semibold">{t('guide.mcp.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('guide.mcp.intro')}</p>
      </div>
      {isOwner ? null : (
        <div role="note" className="flex gap-3 rounded-xl border border-amber-500/50 bg-amber-50 p-4 text-sm dark:bg-amber-950/30">
          <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-amber-600" />
          <p className="font-medium">{t('guide.mcp.staffNote')}</p>
        </div>
      )}

      <section className="flex flex-col gap-3" aria-labelledby="mcp-setup">
        <h2 id="mcp-setup" className="font-heading text-lg font-semibold">
          {t('guide.mcp.setupTitle')}
        </h2>
        <ServerUrlCard />
        <div className="grid gap-3 md:grid-cols-2">
          {CLIENTS.map((client) => (
            <Card key={client.key} size="sm">
              <CardHeader>
                <CardTitle className="text-base">{t(`guide.mcp.${client.key}.title`)}</CardTitle>
                <CardDescription>{t(`guide.mcp.${client.key}.requirement`)}</CardDescription>
              </CardHeader>
              <CardContent>
                <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm">
                  {Array.from({ length: client.steps }, (_, index) => (
                    <li key={index}>{t(`guide.mcp.${client.key}.steps.${index}`)}</li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <BulletCard part="canDo" count={3} />

      <section className="flex flex-col gap-3" aria-labelledby="mcp-examples">
        <h2 id="mcp-examples" className="font-heading text-lg font-semibold">
          {t('guide.mcp.examples.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('guide.mcp.examples.intro')}</p>
        <div className="flex flex-col gap-3">
          {EXAMPLES.map((key, index) => (
            <Card key={key} size="sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                    {index + 1}
                  </span>
                  {t(`guide.mcp.examples.items.${key}.title`)}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-3 text-sm">
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-muted-foreground">{t('guide.mcp.examples.prompt')}</span>
                  <blockquote className="rounded-lg border bg-muted/40 px-3 py-2">
                    {t(`guide.mcp.examples.items.${key}.prompt`)}
                  </blockquote>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-muted-foreground">{t('guide.mcp.examples.result')}</span>
                  <p className="text-muted-foreground">{t(`guide.mcp.examples.items.${key}.result`)}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <div className="grid gap-3 md:grid-cols-2">
        <BulletCard part="afterCreate" count={3} />
        <BulletCard part="troubleshooting" count={4} />
      </div>

      <p className="text-sm text-muted-foreground">
        {t('guide.mcp.manage')}{' '}
        <Link
          to="/w/$slug/account"
          params={{ slug }}
          className="font-medium text-foreground underline underline-offset-4 hover:text-primary"
        >
          {t('guide.mcp.manageLink')}
        </Link>
      </p>
    </section>
  )
}

// The URL to paste into the AI app: this origin, so it is right in every environment.
function ServerUrlCard() {
  const { t } = useTranslation()
  const serverUrl = `${window.location.origin}/mcp`
  const [copied, setCopied] = useState(false)

  function copyUrl() {
    void navigator.clipboard?.writeText(serverUrl).then(() => setCopied(true))
  }

  return (
    <Card size="sm">
      <CardContent className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm font-medium">{t('guide.mcp.serverUrl')}</span>
          <code className="font-mono text-sm break-all">{serverUrl}</code>
        </div>
        <Button variant="outline" className="h-11 shrink-0 self-start sm:self-auto" onClick={copyUrl}>
          {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
          {t(copied ? 'guide.mcp.copied' : 'guide.mcp.copy')}
        </Button>
      </CardContent>
    </Card>
  )
}

function BulletCard({ part, count }: { part: 'canDo' | 'afterCreate' | 'troubleshooting'; count: number }) {
  const { t } = useTranslation()

  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle className="text-base">{t(`guide.mcp.${part}.title`)}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="flex list-disc flex-col gap-2 pl-5 text-sm text-muted-foreground">
          {Array.from({ length: count }, (_, index) => (
            <li key={index}>{t(`guide.mcp.${part}.items.${index}`)}</li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}
